import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  PageHeader,
  ErrorState,
  EmptyState,
  FormField,
  PageViewerControls,
  TextInput,
} from '../../components/primitives.jsx';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useOptionalSiteAccess } from '../../app/SiteAccessContext.jsx';
import { isRootAdminIdentity } from '../../app/rootAdminIdentity.js';
import { IconClipboard } from '../../components/icons.jsx';
import { api } from '../../lib/apiClient.js';
import { useClientConfig } from '../../lib/clientConfig.js';
import { useApi, useMutation } from '../../lib/useApi.js';
import { useUrlPagination } from '../../lib/usePagination.js';
import './admin.css';
import './rbacUsers.redesign.css';

const MANAGED_TESTER_PRESET = 'sandbox_program_manager';

const REQUEST_TABS = [
  { id: 'pending', label: 'Pending' },
  { id: 'approved', label: 'Approved' },
  { id: 'dismissed', label: 'Dismissed' },
];

// Sandbox <select> options shared by the create form and the approval flow.
// Occupied sandboxes render disabled with a plain-language holder label so the
// admin can see who currently holds each private UAT sandbox. When the email
// being created/approved IS the holder (recreating a deleted tester whose
// registry entry still routes them to that sandbox), the option stays
// selectable — the server allows that same-email reassignment.
function renderSandboxOptions(options, candidateEmail = '') {
  const normalizedCandidate = String(candidateEmail || '')
    .trim()
    .toLowerCase();
  return options.map((option) => {
    const occupied = option.occupied === true;
    const holderEmail = String(option.holderEmail || '')
      .trim()
      .toLowerCase();
    const heldByCandidate = occupied && holderEmail !== '' && holderEmail === normalizedCandidate;
    let label = `${option.sandboxLabel} (${option.sandboxId})`;
    if (heldByCandidate) {
      label = `${option.sandboxLabel} — currently assigned to this email`;
    } else if (occupied && option.holderLabel) {
      label = `${option.sandboxLabel} — assigned to ${option.holderLabel}`;
    }
    return (
      <option
        key={option.sandboxId}
        value={option.sandboxId}
        disabled={occupied && !heldByCandidate}
      >
        {label}
      </option>
    );
  });
}

// Compact relative time for the live-status columns; the absolute timestamp
// rides along in the cell's title attribute. Mirrors the audit-log rail's
// relTime so the two admin surfaces read the same way.
function relTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const diff = (Date.now() - parsed.getTime()) / 1000;
  if (diff < 60) return `${Math.max(1, Math.round(diff))} s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)} m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return parsed.toLocaleString();
}

function absoluteTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
}

function shortLocalDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
}

function formatReviewedDate(value) {
  if (!value) return 'Not recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not recorded';
  return parsed.toLocaleString();
}

function authModeTone(value) {
  return value ? 'sage' : 'outline';
}

function formatSandboxLabel(assignment) {
  if (!assignment) return 'No active sandbox assignment';
  const label = assignment.sandboxLabel || assignment.label || assignment.sandboxId || null;
  const id = assignment.sandboxId || assignment.id || null;
  if (label && id && label !== id) return `${label} (${id})`;
  return label || id || 'No active sandbox assignment';
}

function mergeUser(currentUsers, nextUser) {
  const list = Array.isArray(currentUsers) ? [...currentUsers] : [];
  const index = list.findIndex((entry) => entry.id === nextUser.id);
  if (index === -1) {
    return [nextUser, ...list];
  }
  list[index] = { ...list[index], ...nextUser };
  return list;
}

function updateUser(users, userId, updater) {
  return (users || []).map((user) => (user.id === userId ? updater(user) : user));
}

function removeUsers(users, removedUserIds) {
  const removed = new Set(removedUserIds);
  return (users || []).filter((user) => !removed.has(user.id));
}

function removePublicAccessRequest(currentData, requestId) {
  if (!currentData) return currentData;
  const currentItems = currentData.data || [];
  const nextItems = currentItems.filter((item) => item.id !== requestId);
  const nextCounts = {
    ...(currentData.meta?.counts || {}),
    pending: Math.max(0, Number(currentData.meta?.counts?.pending || currentItems.length) - 1),
  };
  return {
    ...currentData,
    data: nextItems,
    page: currentData.page
      ? {
          ...currentData.page,
          total: Math.max(0, Number(currentData.page.total || currentItems.length) - 1),
        }
      : currentData.page,
    meta: {
      ...(currentData.meta || {}),
      counts: nextCounts,
    },
  };
}

export function UserAdminPage() {
  const { user: currentUser } = useCurrentUser();
  const siteAccessState = useOptionalSiteAccess()?.state || null;
  const isRootAdmin = isRootAdminIdentity(siteAccessState);
  const [params, setParams] = useSearchParams();
  const pager = useUrlPagination(params, setParams, { defaultPageSize: 10 });
  const users = useApi('/admin/users', {
    query: { limit: pager.pageSize, offset: pager.offset, search: params.get('search') || '' },
  });
  const authConfig = useApi('/admin/authentication');
  // Fetched independently of the roster so the table renders instantly; cells
  // show a muted em-dash placeholder until this resolves.
  const liveStatus = useApi('/admin/users/live-status');
  const clientConfig = useClientConfig();
  const publicAccessRequests = useApi('/admin/public-access-requests', {
    query: { status: 'pending' },
  });
  const approvedRequests = useApi('/admin/public-access-requests', {
    query: { status: 'approved' },
  });
  const dismissedRequests = useApi('/admin/public-access-requests', {
    query: { status: 'dismissed' },
  });
  const createUser = useMutation();
  const actionMutation = useMutation();
  const [draft, setDraft] = useState({
    displayName: '',
    email: '',
    accessPreset: MANAGED_TESTER_PRESET,
    sandboxId: '',
  });
  const [requestDrafts, setRequestDrafts] = useState({});
  const [edipnDrafts, setEdipnDrafts] = useState({});
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [flash, setFlash] = useState(null);
  const [requestTab, setRequestTab] = useState('pending');

  const items = users.data?.data || [];
  const liveStatusItems = liveStatus.data?.data?.items;
  const liveStatusByUser = useMemo(() => {
    const map = new Map();
    for (const item of liveStatusItems || []) {
      map.set(item.userId, item);
    }
    return map;
  }, [liveStatusItems]);
  const page = users.data?.page || { limit: pager.pageSize, offset: 0, total: items.length };
  const effectiveMode = authConfig.data?.data?.effectiveMode || 'password_first_hybrid';
  const currentUserId = currentUser?.id || currentUser?.userId || null;
  const pendingRequestsData = publicAccessRequests.data?.data;
  const pendingRequests = useMemo(() => pendingRequestsData || [], [pendingRequestsData]);
  const requestCounts = publicAccessRequests.data?.meta?.counts || {
    pending: pendingRequests.length,
    approved: 0,
    dismissed: 0,
  };
  const sandboxOptions = publicAccessRequests.data?.meta?.sandboxOptions || [];
  const sandboxFrontDoorEnabled =
    clientConfig?.sandbox?.frontDoorEnabled === true ||
    clientConfig?.sandbox?.mode === 'front_door';
  const approvedItems = approvedRequests.data?.data || [];
  const dismissedItems = dismissedRequests.data?.data || [];
  const selectableUserIds = items
    .filter((user) => user.id !== currentUserId)
    .map((user) => user.id);
  const allSelectableChecked =
    selectableUserIds.length > 0 &&
    selectableUserIds.every((userId) => selectedUserIds.includes(userId));
  const selectedVisibleCount = selectedUserIds.filter((userId) =>
    selectableUserIds.includes(userId),
  ).length;

  const summary = {
    total: items.length,
    linkedCac: items.filter((user) => user.auth?.linkedEdipn).length,
    passwordEnabled: items.filter((user) => user.auth?.passwordEnabled).length,
    pendingPublicAccess: requestCounts.pending || 0,
  };

  useEffect(() => {
    if (!pendingRequests.length) return;
    setRequestDrafts((current) => {
      const next = { ...current };
      for (const request of pendingRequests) {
        if (!next[request.id]) {
          next[request.id] = {
            displayName: request.fullName || '',
            sandboxId: '',
            decisionNote: '',
          };
        }
      }
      return next;
    });
  }, [pendingRequests]);

  // Every admin action funnels its failure into the same flash strip the
  // successes use — a rejected request must never fail silently (the server
  // messages are already written in plain language, e.g. "That sandbox is
  // already assigned to …").
  function withActionErrorFlash(handler) {
    return async (...args) => {
      try {
        return await handler(...args);
      } catch (error) {
        setFlash({
          tone: 'amber',
          message: error?.message || 'The action could not be completed. Try again.',
        });
        return undefined;
      }
    };
  }

  const handleCreateUser = withActionErrorFlash(async (event) => {
    event.preventDefault();
    setFlash(null);
    if (sandboxFrontDoorEnabled && !draft.sandboxId) {
      setFlash({
        tone: 'amber',
        message: 'Assign a sandbox before creating this managed tester.',
      });
      return;
    }
    const payload = {
      displayName: draft.displayName,
      email: draft.email,
      accessPreset: draft.accessPreset,
    };
    if (draft.sandboxId) {
      payload.sandboxId = draft.sandboxId;
    }
    const response = await createUser.run(() => api.post('/admin/users', payload));
    const nextUser = response?.data || response;
    users.setData((current) => ({
      ...(current || {}),
      data: mergeUser(current?.data, nextUser),
      page: current?.page
        ? { ...current.page, total: Number(current.page.total || 0) + 1 }
        : { limit: pager.pageSize, offset: 0, total: 1 },
    }));
    setDraft({ displayName: '', email: '', accessPreset: MANAGED_TESTER_PRESET, sandboxId: '' });
    setFlash({
      tone: 'sage',
      message: `${nextUser.displayName} was created as an active Program Manager sandbox tester.`,
    });
  });

  const handleIssueTemporaryPassword = withActionErrorFlash(async (userId) => {
    setFlash(null);
    const user = items.find((entry) => entry.id === userId);
    const actionPath =
      user?.auth?.isTemporaryPassword === true
        ? `/admin/users/${userId}/passwords/reset-temporary`
        : `/admin/users/${userId}/passwords/issue-temporary`;
    const response = await actionMutation.run(() => api.post(actionPath, {}));
    const result = response?.data || response;
    users.setData((current) => ({
      ...(current || {}),
      data: updateUser(current?.data, userId, (user) => ({
        ...user,
        assignment: result?.sandbox
          ? {
              ...(user.assignment || {}),
              sandboxId: result.sandbox.id || user.assignment?.sandboxId || null,
              sandboxLabel:
                result.sandbox.label || user.assignment?.sandboxLabel || result.sandbox.id || null,
            }
          : user.assignment,
        auth: {
          ...(user.auth || {}),
          passwordEnabled: result?.passwordEnabled !== false,
          mustChangePassword: result?.mustChangePassword !== false,
          isTemporaryPassword: result?.isTemporaryPassword !== false,
        },
      })),
    }));
    const sandboxLabel = formatSandboxLabel(
      result?.sandbox
        ? {
            sandboxId: result.sandbox.id || null,
            sandboxLabel: result.sandbox.label || null,
          }
        : user?.assignment,
    );
    setFlash({
      tone: 'amber',
      message: `Temporary password for ${user?.displayName || userId} in ${sandboxLabel}: ${result.temporaryPassword}`,
    });
  });

  const handleTogglePassword = withActionErrorFlash(async (userId, enabled) => {
    setFlash(null);
    const path = enabled ? 'enable' : 'disable';
    const user = items.find((entry) => entry.id === userId);
    const response = await actionMutation.run(() =>
      api.post(`/admin/users/${userId}/password-auth/${path}`, {}),
    );
    const result = response?.data || response;
    users.setData((current) => ({
      ...(current || {}),
      data: updateUser(current?.data, userId, (user) => ({
        ...user,
        assignment: result?.sandbox
          ? {
              ...(user.assignment || {}),
              sandboxId: result.sandbox.id || user.assignment?.sandboxId || null,
              sandboxLabel:
                result.sandbox.label || user.assignment?.sandboxLabel || result.sandbox.id || null,
            }
          : user.assignment,
        auth: {
          ...(user.auth || {}),
          passwordEnabled: result?.passwordEnabled === true,
          mustChangePassword: Boolean(result?.mustChangePassword),
          isTemporaryPassword: Boolean(result?.isTemporaryPassword),
        },
      })),
    }));
    const sandboxLabel = formatSandboxLabel(
      result?.sandbox
        ? {
            sandboxId: result.sandbox.id || null,
            sandboxLabel: result.sandbox.label || null,
          }
        : user?.assignment,
    );
    setFlash({
      tone: enabled ? 'sage' : 'amber',
      message: enabled
        ? `Password sign-in was enabled for ${user?.displayName || userId} in ${sandboxLabel}.`
        : `Password sign-in was disabled for ${user?.displayName || userId} in ${sandboxLabel}.`,
    });
  });

  const handleLinkCac = withActionErrorFlash(async (userId) => {
    const edipn = String(edipnDrafts[userId] || '').trim();
    if (!edipn) return;
    setFlash(null);
    await actionMutation.run(() => api.put(`/admin/users/${userId}/cac-link`, { edipn }));
    users.setData((current) => ({
      ...(current || {}),
      data: updateUser(current?.data, userId, (user) => ({
        ...user,
        auth: {
          ...(user.auth || {}),
          linkedEdipn: edipn,
        },
      })),
    }));
    setFlash({
      tone: 'sage',
      message: `CAC link ${edipn} is now connected to ${userId}.`,
    });
  });

  const handleUnlinkCac = withActionErrorFlash(async (userId) => {
    setFlash(null);
    await actionMutation.run(() => api.delete(`/admin/users/${userId}/cac-link`));
    users.setData((current) => ({
      ...(current || {}),
      data: updateUser(current?.data, userId, (user) => ({
        ...user,
        auth: {
          ...(user.auth || {}),
          linkedEdipn: null,
        },
      })),
    }));
    setEdipnDrafts((current) => ({ ...current, [userId]: '' }));
    setFlash({
      tone: 'amber',
      message: `CAC link was removed from ${userId}.`,
    });
  });

  const handleDismissPublicAccessRequest = withActionErrorFlash(async (requestRecord) => {
    setFlash(null);
    const reviewDraft = requestDrafts[requestRecord.id] || {};
    await actionMutation.run(() =>
      api.post(`/admin/public-access-requests/${requestRecord.id}/actions`, {
        action: 'dismiss',
        decisionNote: reviewDraft.decisionNote || '',
      }),
    );
    publicAccessRequests.setData((current) => removePublicAccessRequest(current, requestRecord.id));
    setFlash({
      tone: 'amber',
      message: `Dismissed the PMHub access request for ${requestRecord.email}.`,
    });
  });

  const handleApprovePublicAccessRequest = withActionErrorFlash(async (requestRecord) => {
    setFlash(null);
    const reviewDraft = requestDrafts[requestRecord.id] || {};
    if (!reviewDraft.sandboxId) {
      setFlash({
        tone: 'amber',
        message: `Choose a sandbox assignment before approving ${requestRecord.email}.`,
      });
      return;
    }

    await actionMutation.run(() =>
      api.post(`/admin/public-access-requests/${requestRecord.id}/actions`, {
        action: 'approve',
        displayName: reviewDraft.displayName || requestRecord.fullName,
        sandboxId: reviewDraft.sandboxId,
        decisionNote: reviewDraft.decisionNote || '',
      }),
    );
    publicAccessRequests.setData((current) => removePublicAccessRequest(current, requestRecord.id));
    users.refresh?.();
    setFlash({
      tone: 'sage',
      message: `Approved ${requestRecord.email} and created the managed tester on ${reviewDraft.sandboxId}.`,
    });
  });

  function openSingleDelete(user) {
    setPendingDelete({
      mode: 'single',
      userIds: [user.id],
      title: 'Delete account?',
      confirmLabel: 'Delete account',
      description: `Delete ${user.displayName}'s managed tester account. This removes PRIME sign-in records, role grants, active sessions, and any linked CAC association. Existing product history is not scrubbed.`,
    });
  }

  function openBulkDelete() {
    if (!selectedVisibleCount) return;
    setPendingDelete({
      mode: 'bulk',
      userIds: selectedUserIds.filter((userId) => selectableUserIds.includes(userId)),
      title: 'Delete selected testers?',
      confirmLabel: 'Delete selected',
      description:
        'Delete the selected managed tester accounts. This removes PRIME sign-in records, role grants, active sessions, and any linked CAC association for those testers only.',
    });
  }

  const handleConfirmDelete = withActionErrorFlash(async () => {
    if (!pendingDelete) return;
    setFlash(null);
    if (pendingDelete.mode === 'single') {
      const userId = pendingDelete.userIds[0];
      const response = await actionMutation.run(() => api.delete(`/admin/users/${userId}`));
      const result = response?.data || response;
      users.setData((current) => {
        const nextData = removeUsers(current?.data, [userId]);
        const currentTotal = Number(current?.page?.total || current?.data?.length || 0);
        return {
          ...(current || {}),
          data: nextData,
          page: current?.page
            ? { ...current.page, total: Math.max(0, currentTotal - 1) }
            : { limit: pager.pageSize, offset: 0, total: nextData.length },
        };
      });
      setSelectedUserIds((current) => current.filter((entry) => entry !== userId));
      setFlash({
        tone: 'amber',
        message: `${result.userId || userId} was deleted from the managed tester roster.`,
      });
    } else {
      const response = await actionMutation.run(() =>
        api.post('/admin/users/bulk-delete', { userIds: pendingDelete.userIds }),
      );
      const result = response?.data || response;
      const deletedUserIds = result.deletedUserIds || [];
      users.setData((current) => {
        const nextData = removeUsers(current?.data, deletedUserIds);
        const currentTotal = Number(current?.page?.total || current?.data?.length || 0);
        return {
          ...(current || {}),
          data: nextData,
          page: current?.page
            ? { ...current.page, total: Math.max(0, currentTotal - deletedUserIds.length) }
            : { limit: pager.pageSize, offset: 0, total: nextData.length },
        };
      });
      setSelectedUserIds((current) => current.filter((entry) => !deletedUserIds.includes(entry)));
      const blockedSummary =
        result.blockedUserIds?.length || result.notFoundUserIds?.length
          ? ` Blocked: ${(result.blockedUserIds || []).length}. Missing: ${(result.notFoundUserIds || []).length}.`
          : '';
      setFlash({
        tone: 'amber',
        message: `${deletedUserIds.length} managed tester account(s) deleted.${blockedSummary}`,
      });
    }
    setPendingDelete(null);
  });

  function toggleUserSelection(userId, checked) {
    setSelectedUserIds((current) => {
      const next = new Set(current);
      if (checked) next.add(userId);
      else next.delete(userId);
      return [...next];
    });
  }

  function toggleAllSelection(checked) {
    setSelectedUserIds(checked ? [...selectableUserIds] : []);
  }

  if (users.error && !users.data) {
    return <ErrorState error={users.error} />;
  }

  return (
    <div className="users-redesign user-admin-page">
      <PageHeader
        eyebrow="Administration"
        title="User administration"
        subtitle="Create PRIME accounts, issue temporary passwords, and manage CAC/password posture without leaving the admin shell."
        actions={
          isRootAdmin ? (
            <Link className="btn primary" to="/admin/guide-results">
              <IconClipboard size={18} />
              Review PM guide results
            </Link>
          ) : null
        }
      />

      <section className="bento admin-bento user-admin-layout">
        <div className="span-4 card user-admin-card">
          <div className="eyebrow">Account creation</div>
          <h2 className="card-title">Create managed tester</h2>
          <p className="muted small user-admin-lede">
            Every created tester becomes an active Program Manager sandbox user with a `/home`
            landing. Pending-access setup is no longer used for UAT testers.
          </p>
          <div className="banner blue">
            Program Manager sandbox access only. Cross-user admin, audit, and activity review remain
            reserved for the root admin operator.
          </div>

          <form className="user-admin-form" onSubmit={handleCreateUser}>
            <FormField label="Display name" htmlFor="managed-user-display-name" required>
              <TextInput
                id="managed-user-display-name"
                value={draft.displayName}
                onChange={(value) => setDraft((current) => ({ ...current, displayName: value }))}
              />
            </FormField>
            <FormField label="Email address" htmlFor="managed-user-email" required>
              <TextInput
                id="managed-user-email"
                value={draft.email}
                onChange={(value) => setDraft((current) => ({ ...current, email: value }))}
              />
            </FormField>
            {sandboxFrontDoorEnabled ? (
              <FormField
                label="Sandbox assignment"
                htmlFor="managed-user-sandbox"
                required={sandboxFrontDoorEnabled}
                hint="Each tester holds exactly one sandbox. Occupied sandboxes are shown but cannot be reused until their tester is removed."
              >
                <select
                  id="managed-user-sandbox"
                  className="form-select"
                  value={draft.sandboxId}
                  onChange={(event) =>
                    setDraft((current) => ({ ...current, sandboxId: event.target.value }))
                  }
                >
                  <option value="">Select sandbox…</option>
                  {renderSandboxOptions(sandboxOptions, draft.email)}
                </select>
              </FormField>
            ) : null}
            <button className="btn primary" type="submit" disabled={createUser.running}>
              {createUser.running ? 'Creating…' : 'Create account'}
            </button>
          </form>
        </div>

        <div className="span-8 card user-admin-card">
          <div className="eyebrow">Authentication posture</div>
          <h2 className="card-title">Current auth operating mode</h2>
          <div className="user-admin-mode-strip">
            <div className="user-admin-mode-chip">
              <span className="chip blue sm">
                {effectiveMode === 'cac_only' ? 'CAC only' : 'Password-first hybrid'}
              </span>
              <span className="muted small">
                Existing password sessions continue naturally even after a switch to CAC-only.
              </span>
            </div>
            <div className="user-admin-summary-grid">
              <div className="user-admin-summary-card">
                <span>Total users</span>
                <strong>{summary.total}</strong>
              </div>
              <div className="user-admin-summary-card">
                <span>CAC linked</span>
                <strong>{summary.linkedCac}</strong>
              </div>
              <div className="user-admin-summary-card">
                <span>Password enabled</span>
                <strong>{summary.passwordEnabled}</strong>
              </div>
              <div className="user-admin-summary-card">
                <span>Pending PMHub requests</span>
                <strong>{summary.pendingPublicAccess}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="span-12 card user-admin-card">
          <div className="row items-center justify-between wrap">
            <div>
              <div className="eyebrow">PMHub access intake</div>
              <h2 className="card-title">PMHub access requests</h2>
              <p className="muted small user-admin-lede">
                Unknown public-host emails land in Request access. Review pending requests here,
                assign the sandbox, and create the managed tester from the same workflow. Approved
                and dismissed decisions are kept for the record.
              </p>
            </div>
            <div className="row items-center gap-8 wrap">
              <span className="chip outline sm">{sandboxOptions.length} sandbox options</span>
            </div>
          </div>

          <div
            className="user-admin-tabs"
            role="tablist"
            aria-label="PMHub access request decisions"
          >
            {REQUEST_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={requestTab === tab.id}
                className={`user-admin-tab${requestTab === tab.id ? ' is-active' : ''}`}
                onClick={() => setRequestTab(tab.id)}
              >
                {tab.label}
                <span className="user-admin-tab-count">{requestCounts[tab.id] || 0}</span>
              </button>
            ))}
          </div>

          {requestTab !== 'pending' ? (
            (() => {
              const historyState = requestTab === 'approved' ? approvedRequests : dismissedRequests;
              const historyItems = requestTab === 'approved' ? approvedItems : dismissedItems;
              if (historyState.error && !historyState.data) {
                return <ErrorState error={historyState.error} />;
              }
              if (historyItems.length === 0) {
                return (
                  <EmptyState
                    title={`No ${requestTab} PMHub access requests`}
                    description={
                      requestTab === 'approved'
                        ? 'Approved requests and their assigned sandbox will be listed here for the record.'
                        : 'Dismissed requests will be listed here for the record.'
                    }
                  />
                );
              }
              return (
                <div className="user-admin-request-list">
                  {historyItems.map((record) => (
                    <article key={record.id} className="user-admin-request-card">
                      <div className="user-admin-request-head">
                        <div>
                          <div className="eyebrow">
                            {requestTab === 'approved' ? 'Approved request' : 'Dismissed request'}
                          </div>
                          <h3 className="card-title">{record.fullName || record.email}</h3>
                          <p className="muted small">
                            {record.email} · {record.organization || 'No organization provided'}
                          </p>
                        </div>
                        <span
                          className={`chip ${requestTab === 'approved' ? 'sage' : 'outline'} sm`}
                        >
                          {requestTab === 'approved' ? 'Approved' : 'Dismissed'}
                        </span>
                      </div>
                      <div className="user-admin-request-grid">
                        <div className="user-admin-request-fact">
                          <span>Decision note</span>
                          <strong>{record.decisionNote || 'None recorded'}</strong>
                        </div>
                        <div className="user-admin-request-fact">
                          <span>Reviewed</span>
                          <strong>{formatReviewedDate(record.reviewedAt)}</strong>
                        </div>
                        {requestTab === 'approved' ? (
                          <div className="user-admin-request-fact">
                            <span>Assigned sandbox</span>
                            <strong>{formatSandboxLabel(record.sandboxAssignment)}</strong>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              );
            })()
          ) : publicAccessRequests.error && !publicAccessRequests.data ? (
            <ErrorState error={publicAccessRequests.error} />
          ) : pendingRequests.length === 0 ? (
            <EmptyState
              title="No pending PMHub access requests"
              description="Unknown-email Request access submissions will appear here for sandbox assignment and tester creation."
            />
          ) : (
            <div className="user-admin-request-list">
              {pendingRequests.map((request) => {
                const reviewDraft = requestDrafts[request.id] || {
                  displayName: request.fullName || '',
                  sandboxId: '',
                  decisionNote: '',
                };
                return (
                  <article key={request.id} className="user-admin-request-card">
                    <div className="user-admin-request-head">
                      <div>
                        <div className="eyebrow">Pending request</div>
                        <h3 className="card-title">{request.fullName || request.email}</h3>
                        <p className="muted small">
                          {request.email} · {request.organization || 'No organization provided'}
                        </p>
                      </div>
                      <span className="chip blue sm">Request access</span>
                    </div>

                    <div className="user-admin-request-grid">
                      <div className="user-admin-request-fact">
                        <span>Justification</span>
                        <strong>{request.justification}</strong>
                      </div>
                      <div className="user-admin-request-fact">
                        <span>Expected access note</span>
                        <strong>{request.expectedTesterAccessNote || 'None provided'}</strong>
                      </div>
                    </div>

                    <div className="user-admin-request-form">
                      <FormField
                        label="Display name"
                        htmlFor={`request-display-name-${request.id}`}
                        required
                      >
                        <TextInput
                          id={`request-display-name-${request.id}`}
                          value={reviewDraft.displayName}
                          onChange={(value) =>
                            setRequestDrafts((current) => ({
                              ...current,
                              [request.id]: { ...reviewDraft, displayName: value },
                            }))
                          }
                        />
                      </FormField>

                      <FormField
                        label="Assign sandbox"
                        htmlFor={`request-sandbox-${request.id}`}
                        required
                      >
                        <select
                          id={`request-sandbox-${request.id}`}
                          className="form-select"
                          value={reviewDraft.sandboxId}
                          onChange={(event) =>
                            setRequestDrafts((current) => ({
                              ...current,
                              [request.id]: { ...reviewDraft, sandboxId: event.target.value },
                            }))
                          }
                        >
                          <option value="">Select sandbox…</option>
                          {renderSandboxOptions(sandboxOptions, request.email)}
                        </select>
                      </FormField>

                      <FormField label="Decision note" htmlFor={`request-note-${request.id}`}>
                        <textarea
                          id={`request-note-${request.id}`}
                          className="form-input user-admin-request-note"
                          value={reviewDraft.decisionNote}
                          onChange={(event) =>
                            setRequestDrafts((current) => ({
                              ...current,
                              [request.id]: { ...reviewDraft, decisionNote: event.target.value },
                            }))
                          }
                        />
                      </FormField>
                    </div>

                    <div className="user-admin-request-actions">
                      <button
                        type="button"
                        className="btn primary"
                        disabled={actionMutation.running || !reviewDraft.sandboxId}
                        onClick={() => handleApprovePublicAccessRequest(request)}
                      >
                        Approve and create tester
                      </button>
                      <button
                        type="button"
                        className="btn ghost"
                        disabled={actionMutation.running}
                        onClick={() => handleDismissPublicAccessRequest(request)}
                      >
                        Dismiss request
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <div className="span-12 card user-admin-card">
          <div className="row items-center justify-between wrap">
            <div>
              <div className="eyebrow">Managed testers</div>
              <h2 className="card-title">Sandbox tester accounts and sign-in controls</h2>
            </div>
            <div className="row items-center gap-8 wrap">
              <span className="chip outline sm">{selectedVisibleCount} selected</span>
              <button
                type="button"
                className="btn danger sm"
                disabled={!selectedVisibleCount}
                onClick={openBulkDelete}
              >
                Delete selected
              </button>
              {flash ? (
                <div
                  className={`banner ${flash.tone === 'sage' ? 'green' : 'amber'} user-admin-flash`}
                >
                  {flash.message}
                </div>
              ) : null}
            </div>
          </div>

          {items.length === 0 ? (
            <EmptyState
              title="No managed testers"
              description="Create the first managed tester to start issuing PRIME sandbox credentials."
            />
          ) : (
            <div className="user-admin-table-wrap">
              <table className="table users-table user-admin-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        aria-label="Select all managed testers"
                        checked={allSelectableChecked}
                        onChange={(event) => toggleAllSelection(event.target.checked)}
                      />
                    </th>
                    <th>User</th>
                    <th>Access</th>
                    <th>Password sign-in</th>
                    <th>Last login</th>
                    <th style={{ textAlign: 'right' }}>Clicks</th>
                    <th>Linked CAC</th>
                    <th>Landing</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${user.displayName}`}
                          checked={selectedUserIds.includes(user.id)}
                          disabled={user.id === currentUserId}
                          onChange={(event) => toggleUserSelection(user.id, event.target.checked)}
                        />
                      </td>
                      <td>
                        <strong>{user.displayName}</strong>
                        <div className="muted small">{user.email || user.id}</div>
                        <div className="muted small">
                          Assigned sandbox: {formatSandboxLabel(user.assignment)}
                        </div>
                        {user.id === currentUserId ? (
                          <div className="muted small">Signed-in admin</div>
                        ) : null}
                      </td>
                      <td>
                        <div className="user-admin-auth-stack">
                          <span className="chip sage sm">Active</span>
                          <span className="chip blue sm">Program Manager sandbox access</span>
                        </div>
                      </td>
                      <td>
                        {(() => {
                          const live = liveStatusByUser.get(user.id) || null;
                          // A live read is authoritative; the roster's shadow
                          // credential goes stale the moment a tester changes
                          // their own password in their sandbox. A mirror row
                          // keeps the shadow chips and flags its own staleness.
                          const isLive = live?.source === 'live';
                          const passwordEnabled = isLive
                            ? live.passwordEnabled
                            : user.auth?.passwordEnabled;
                          const mustChange = isLive
                            ? live.mustChangePassword
                            : user.auth?.mustChangePassword;
                          const isTemporary = isLive
                            ? live.isTemporaryPassword
                            : user.auth?.isTemporaryPassword;
                          const passwordSet =
                            isLive && passwordEnabled && !mustChange && !isTemporary;
                          return (
                            <div className="user-admin-auth-stack">
                              <span className={`chip ${authModeTone(passwordEnabled)} sm`}>
                                {passwordEnabled ? 'Enabled' : 'Disabled'}
                              </span>
                              {passwordSet ? (
                                <span className="chip sage sm" data-testid="chip-password-set">
                                  Password set
                                </span>
                              ) : (
                                <>
                                  {mustChange ? (
                                    <span className="chip amber sm">Must change password</span>
                                  ) : null}
                                  {isTemporary ? (
                                    <span className="chip blue sm">Temporary issued</span>
                                  ) : null}
                                </>
                              )}
                              {live?.source === 'mirror' ? (
                                <span className="muted small" data-testid="live-status-asof">
                                  as of {shortLocalDateTime(live.liveAsOf) || 'account creation'}
                                </span>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        {(() => {
                          const live = liveStatusByUser.get(user.id) || null;
                          if (!live) {
                            return (
                              <span className="muted small" data-testid="live-status-last-login">
                                —
                              </span>
                            );
                          }
                          if (!live.lastLoginAt) {
                            return (
                              <span className="muted small" data-testid="live-status-last-login">
                                Never
                              </span>
                            );
                          }
                          return (
                            <span
                              data-testid="live-status-last-login"
                              title={absoluteTime(live.lastLoginAt)}
                            >
                              {relTime(live.lastLoginAt)}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {(() => {
                          const live = liveStatusByUser.get(user.id) || null;
                          if (!live) {
                            return (
                              <span className="muted small" data-testid="live-status-clicks">
                                —
                              </span>
                            );
                          }
                          return (
                            <span data-testid="live-status-clicks">{Number(live.clicks || 0)}</span>
                          );
                        })()}
                      </td>
                      <td>
                        {user.auth?.linkedEdipn ? (
                          <span className="persona-chip tone-blue">
                            <span className="persona-chip__dot" />
                            {user.auth.linkedEdipn}
                          </span>
                        ) : (
                          <span className="muted small">Not linked</span>
                        )}
                      </td>
                      <td>{user.defaultLandingRoute || '—'}</td>
                      <td>
                        <div className="user-admin-actions">
                          <div className="user-admin-action-row">
                            <button
                              type="button"
                              className="btn ghost sm"
                              onClick={() => handleIssueTemporaryPassword(user.id)}
                            >
                              {user.auth?.isTemporaryPassword
                                ? 'Reset temporary password'
                                : 'Issue temporary password'}
                            </button>
                            <button
                              type="button"
                              className="btn secondary sm"
                              onClick={() =>
                                handleTogglePassword(user.id, user.auth?.passwordEnabled !== true)
                              }
                            >
                              {user.auth?.passwordEnabled ? 'Disable password' : 'Enable password'}
                            </button>
                          </div>
                          <div className="user-admin-link-row">
                            <input
                              type="text"
                              className="form-input user-admin-edipn-input"
                              inputMode="numeric"
                              placeholder="10-digit EDIPN"
                              value={edipnDrafts[user.id] ?? user.auth?.linkedEdipn ?? ''}
                              onChange={(event) =>
                                setEdipnDrafts((current) => ({
                                  ...current,
                                  [user.id]: event.target.value,
                                }))
                              }
                            />
                            <button
                              type="button"
                              className="btn ghost sm"
                              onClick={() => handleLinkCac(user.id)}
                            >
                              {user.auth?.linkedEdipn ? 'Update CAC link' : 'Link CAC'}
                            </button>
                            {user.auth?.linkedEdipn ? (
                              <button
                                type="button"
                                className="btn ghost sm"
                                onClick={() => handleUnlinkCac(user.id)}
                              >
                                Unlink CAC
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn danger sm"
                              disabled={user.id === currentUserId}
                              onClick={() => openSingleDelete(user)}
                            >
                              Delete account
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <PageViewerControls
        page={page}
        label="users"
        pageSize={pager.pageSize}
        onOffsetChange={pager.setOffset}
        onPageSizeChange={pager.setPageSize}
      />

      {pendingDelete ? (
        <div className="user-admin-confirm-backdrop" onClick={() => setPendingDelete(null)}>
          <section
            className="card user-admin-card user-admin-confirm-dialog"
            role="dialog"
            aria-modal="true"
            aria-label={pendingDelete.title}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="row items-center justify-between wrap">
              <h2 className="card-title">{pendingDelete.title}</h2>
            </div>
            <p className="muted small">{pendingDelete.description}</p>
            <div className="row items-center gap-8 wrap">
              <button type="button" className="btn ghost" onClick={() => setPendingDelete(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={actionMutation.running}
                onClick={handleConfirmDelete}
              >
                {actionMutation.running ? 'Deleting…' : pendingDelete.confirmLabel}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
